// src/views/layouts/MainLayout.jsx
import React, { useEffect, useState, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, Moon, Sun, ChevronDown, User, MessageCircle, ArrowRight, Plus } from 'lucide-react';
import { useScroll } from '../../controllers/hooks/useScroll';
import { useLeadContext } from '../../controllers/context/LeadContext';
import { LEAD_SOURCES, LEAD_TYPES } from '../../models/leadTypes';
import { saveLead } from '../../controllers/services/leadService';
import Button from '../components/Button/Button';
import WhatsAppFloater from '../components/WhatsAppFloater/WhatsAppFloater';
import MobileStickyBar from '../components/MobileStickyBar/MobileStickyBar';
import LeadPopup from '../components/LeadPopup/LeadPopup';
import LoginPopup from '../components/LoginPopup/LoginPopup';
import AnimatedPage from '../components/Motion/AnimatedPage';
import CursorGlow from '../components/Motion/CursorGlow';
import { useAuth } from '../../controllers/context/AuthContext';
import { useSiteSettings } from '../../controllers/context/SiteSettingsContext';
import { generateWhatsAppLink } from '../../controllers/services/leadService';
import { AnimatePresence, motion } from 'framer-motion';
import './layouts.css';
import logoImg from '../../assets/logo.png';
import { ADMIN_PANEL_URL } from '../../controllers/utils/adminPanel';
import { getFooterConstructionServices } from '../../controllers/services/constructionService';

const primaryNavItems = [
  { path: '/', label: 'Home' },
  { path: '/buy', label: 'Buy' },
  { path: '/sell', label: 'Sell' },
  { path: '/rent', label: 'Rent' },
  { path: '/reels', label: 'Reels' },
  { path: '/builders', label: 'Builders' },
  { path: '/construction', label: 'Construction' },
];

const moreNavItems = [
  { path: '/careers', label: 'Careers' },
  { path: '/blogs', label: 'Blogs' },
];

const MainLayout = () => {
  const isScrolled = useScroll(30);
  const location = useLocation();
  const { openPopup } = useLeadContext();
  const { user, openLoginPopup } = useAuth();
  const { whatsappPhone, phoneDisplay, supportEmail, websiteUrl, phoneTel } = useSiteSettings();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle');
  const [footerServices, setFooterServices] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    getFooterConstructionServices().then((data) => {
      if (isMounted && data) {
        setFooterServices(data);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const isMoreActive = ['/careers', '/blogs', '/chat'].includes(location.pathname);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes('@')) return;
    setNewsletterStatus('loading');
    const result = await saveLead({
      email: newsletterEmail,
      source: LEAD_SOURCES.NEWSLETTER,
      type: LEAD_TYPES.NEWSLETTER,
      name: 'Newsletter Subscriber',
    });
    if (result.success) {
      setNewsletterStatus('success');
      setNewsletterEmail('');
      setTimeout(() => setNewsletterStatus('idle'), 4000);
    } else {
      setNewsletterStatus('error');
      setTimeout(() => setNewsletterStatus('idle'), 4000);
    }
  };

  // Close dropdown & mobile menu on location change
  useEffect(() => {
    setIsMoreOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Click outside to close "More" dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsMoreOpen(false);
      }
    };
    if (isMoreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMoreOpen]);

  // Close mobile menu or dropdown on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setIsMoreOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Lead Popup timing & session handling (Part B #12: 8-10s timer or scroll, session-aware)
  useEffect(() => {
    if (sessionStorage.getItem('enquiryPopupDismissed') || sessionStorage.getItem('autoPopupShown')) {
      return;
    }

    const triggerAutoPopup = () => {
      if (!sessionStorage.getItem('enquiryPopupDismissed') && !sessionStorage.getItem('autoPopupShown')) {
        sessionStorage.setItem('autoPopupShown', 'true');
        openPopup('Find Your Property Solution', LEAD_SOURCES.HOME_PAGE_AUTO, LEAD_TYPES.BUYER);
      }
    };

    // 9 second timer
    const timer = setTimeout(triggerAutoPopup, 9000);

    // Scroll trigger (after scrolling down)
    const handleScroll = () => {
      if (window.scrollY > 350) {
        triggerAutoPopup();
        window.removeEventListener('scroll', handleScroll);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Exit Intent Popup
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem('enquiryPopupDismissed') && !sessionStorage.getItem('exitPopupShown')) {
        sessionStorage.setItem('exitPopupShown', 'true');
        openPopup('Find Your Property Solution', LEAD_SOURCES.EXIT_INTENT, LEAD_TYPES.BUYER);
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [openPopup]);

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  return (
    <div className={`main-layout ${location.pathname === '/profile' ? 'on-profile-page' : ''}`}>
      {/* Ambient Cursor Glow */}
      <CursorGlow />

      {/* Global Header (Part A) */}
      <header className={`global-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container header-container">
          {/* Logo Area */}
          <Link to="/" className="header-logo-container" aria-label="No Brokerage Home">
            <img
              src={logoImg}
              alt="No Brokerage"
              className="logo-img header-logo-img"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation Links */}
          <nav className="nav-links" aria-label="Main navigation">
            {primaryNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                >
                  {item.label}
                  {isActive && <span className="nav-item-active-underline" />}
                </Link>
              );
            })}

            {/* "More" Dropdown for Careers, Blogs, and Inbox */}
            <div className="nav-dropdown" ref={dropdownRef}>
              <button
                type="button"
                className={`nav-dropdown-toggle ${isMoreActive ? 'active' : ''}`}
                onClick={() => setIsMoreOpen(!isMoreOpen)}
                aria-expanded={isMoreOpen}
                aria-haspopup="true"
              >
                <span>More</span>
                <ChevronDown
                  size={15}
                  style={{
                    transform: isMoreOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </button>

              <AnimatePresence>
                {isMoreOpen && (
                  <motion.div
                    className="nav-dropdown-menu"
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ duration: 0.18 }}
                  >
                    {moreNavItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`nav-dropdown-item ${location.pathname === item.path ? 'active' : ''}`}
                        onClick={() => setIsMoreOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                    {user && (
                      <Link
                        to="/chat"
                        className={`nav-dropdown-item ${location.pathname === '/chat' ? 'active' : ''}`}
                        onClick={() => setIsMoreOpen(false)}
                      >
                        Inbox
                      </Link>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          {/* Right-Side Actions Group */}
          <div className="header-actions">
            {/* Free Consultation - Primary Solid Brand Red (triggers popup) */}
            <Button
              variant="accent"
              onClick={() => openPopup('Find Your Property Solution', 'header_button', 'solution')}
              className="header-consult-btn"
            >
              Free Consultation
            </Button>

            {/* Chat Now - WhatsApp soft/outline button */}
            <Button
              variant="whatsapp"
              onClick={() => window.open(generateWhatsAppLink('Hi NOBROKERAGE, I want to chat.', whatsappPhone), '_blank')}
              className="header-chat-btn desktop-only-action"
            >
              <MessageCircle size={18} />
              <span>Chat Now</span>
            </Button>

            {/* Login / Profile */}
            {user ? (
              <Link
                to="/profile"
                className="header-user-avatar mobile-header-avatar"
                title={user.full_name || user.name || 'Account'}
                aria-label="User profile"
              >
                <div className="header-user-avatar-badge">
                  {(user.full_name || user.name || 'U').charAt(0).toUpperCase()}
                </div>
              </Link>
            ) : (
              <button
                type="button"
                onClick={openLoginPopup}
                className="btn btn-ghost header-login-btn desktop-only-action"
              >
                <User size={18} />
                <span>Login</span>
              </button>
            )}

            {/* Dark Mode Toggle */}
            <button
              type="button"
              className="header-theme-toggle"
              onClick={toggleTheme}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Mobile Hamburger Toggle (< 1024px) */}
            <button
              type="button"
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Full-Height Drawer Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <motion.div
                className="mobile-nav-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <motion.div
                className="mobile-nav-drawer"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              >
                <div className="mobile-drawer-header">
                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="logo">
                    <img src={logoImg} alt="No Brokerage" className="header-logo-img" style={{ height: '38px' }} />
                  </Link>
                  <button
                    type="button"
                    className="mobile-menu-toggle"
                    onClick={() => setIsMobileMenuOpen(false)}
                    aria-label="Close navigation menu"
                    style={{ display: 'flex' }}
                  >
                    <X size={22} />
                  </button>
                </div>

                {/* Top Prominent User Profile Section (Always visible without scrolling) */}
                {user ? (
                  <div className="mobile-drawer-user-card">
                    <Link
                      to="/profile"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="mobile-drawer-user-card-link"
                    >
                      <div className="mobile-drawer-user-avatar">
                        {(user.full_name || user.name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="mobile-drawer-user-info">
                        <div className="mobile-drawer-user-name">
                          {user.full_name || user.name || 'Sunil Prajapati'}
                        </div>
                        <div className="mobile-drawer-user-sub">
                          {user.phone_number || user.email || 'My Profile & Listings'}
                        </div>
                      </div>
                      <ArrowRight size={18} className="mobile-drawer-user-arrow" />
                    </Link>
                    <div className="mobile-drawer-user-quick-actions">
                      <Link
                        to="/sell"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="drawer-quick-btn drawer-quick-btn-add"
                      >
                        <Plus size={14} />
                        <span>List Property</span>
                      </Link>
                      <Link
                        to="/profile"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="drawer-quick-btn drawer-quick-btn-my"
                      >
                        <span>My Properties</span>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="mobile-drawer-guest-card">
                    <button
                      type="button"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        openLoginPopup();
                      }}
                      className="mobile-drawer-guest-btn"
                    >
                      <div className="mobile-drawer-guest-icon">
                        <User size={20} />
                      </div>
                      <div className="mobile-drawer-guest-text">
                        <span className="guest-title">Login / Register</span>
                        <span className="guest-sub">List properties & track leads</span>
                      </div>
                      <ArrowRight size={18} className="mobile-drawer-user-arrow" />
                    </button>
                  </div>
                )}

                <div className="mobile-drawer-links">
                  {[...primaryNavItems, ...moreNavItems].map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`mobile-nav-item ${location.pathname === item.path ? 'active' : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                  {user && (
                    <Link
                      to="/chat"
                      className={`mobile-nav-item ${location.pathname === '/chat' ? 'active' : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Inbox
                    </Link>
                  )}
                </div>

                <div className="mobile-drawer-footer">
                  <div className="drawer-footer-actions">
                    <Button
                      variant="accent"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        openPopup('Find Your Property Solution', 'mobile_drawer', 'solution');
                      }}
                      className="header-consult-btn drawer-footer-btn"
                    >
                      Consultation
                    </Button>
                    <Button
                      variant="whatsapp"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        window.open(generateWhatsAppLink('Hi NOBROKERAGE, I want to chat.', whatsappPhone), '_blank');
                      }}
                      className="header-chat-btn drawer-footer-btn"
                    >
                      <MessageCircle size={15} />
                      <span>WhatsApp</span>
                    </Button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content Area */}
      <main className="main-content">
        <AnimatePresence mode="wait">
          <AnimatedPage key={location.pathname}>
            <Outlet />
          </AnimatedPage>
        </AnimatePresence>
      </main>

      {/* Global Footer */}
      <footer className="global-footer">
        <div className="footer-top-border"></div>
        <div className="container">
          <div className="footer-grid">
            {/* Brand Column */}
            <div className="footer-col brand-col">
              <Link to="/" className="logo footer-logo">
                <img src={logoImg} alt="NoBrokerage Logo" className="logo-img footer-logo-img" decoding="async" />
              </Link>
              <p className="footer-desc" style={{ marginBottom: '0.5rem', fontWeight: '600' }}>Buy • Sell • Rent • Build</p>
              <p className="footer-desc" style={{ fontSize: '0.88rem', color: 'var(--accent)' }}>Serving Ghazipur | Varanasi | Lucknow</p>
              <div className="social-links">
                <motion.a whileHover={{ y: -4, scale: 1.1 }} href="https://www.facebook.com/share/1M9ksKXpPs/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Facebook"><svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M22.675 0h-21.35C.597 0 0 .597 0 1.325v21.351C0 23.403.597 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.597 1.323-1.324V1.325C24 .597 23.403 0 22.675 0z" /></svg></motion.a>
                <motion.a whileHover={{ y: -4, scale: 1.1 }} href="#!" className="social-icon" aria-label="Twitter"><svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.054 10.054 0 01-3.127 1.184 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06c0 2.39 1.69 4.38 3.946 4.83a4.902 4.902 0 01-2.224.084 4.928 4.928 0 004.6 3.419A9.9 9.9 0 010 19.54a13.94 13.94 0 007.548 2.212c9.057 0 14.01-7.506 14.01-14.01 0-.213-.005-.425-.014-.636A10.025 10.025 0 0024 4.59z" /></svg></motion.a>
                <motion.a whileHover={{ y: -4, scale: 1.1 }} href="https://www.instagram.com/nobrokerages?igsh=ZnhubnFtZW5jeXdl" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram"><svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg></motion.a>
                <motion.a whileHover={{ y: -4, scale: 1.1 }} href="#!" className="social-icon" aria-label="LinkedIn"><svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg></motion.a>
              </div>
            </div>

            {/* Our Services Column (Construction Services Single Source) */}
            <div className="footer-col">
              <h4>Our Services</h4>
              <div className="footer-links">
                {footerServices.slice(0, 7).map((service) => (
                  <Link
                    key={service.id}
                    to={`/construction#service-${service.id}`}
                  >
                    {service.name}
                  </Link>
                ))}
                <Link
                  to="/construction"
                  style={{
                    color: 'var(--brand-red, #A8080C)',
                    fontWeight: '600',
                    marginTop: '2px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  All Services →
                </Link>
              </div>
            </div>

            {/* Quick Links */}
            <div className="footer-col">
              <h4>Quick Links</h4>
              <div className="footer-links">
                <Link to="/about">About Us</Link>
                <Link to="/buy">Properties</Link>
                <Link to="/construction">Construction Services</Link>
                <Link to="/builders">Top Builders</Link>
                <Link to="/careers">Careers & Jobs</Link>
                <Link to="/blogs">Blogs & Articles</Link>
                <Link to="/contact">Contact Us</Link>
              </div>
            </div>

            {/* Legal Links */}
            <div className="footer-col">
              <h4>Legal & Policies</h4>
              <div className="footer-links">
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/terms">Terms & Conditions</Link>
                <Link to="/refund">Refund Policy</Link>
                <Link to="/disclaimer">Disclaimer</Link>
                <Link to="/delete-account">Delete Account</Link>
                {/* The admin panel is a separate site with its own login. */}
                {ADMIN_PANEL_URL && (
                  <a href={ADMIN_PANEL_URL} target="_blank" rel="noopener noreferrer">Admin Login</a>
                )}
              </div>
            </div>

            {/* Newsletter/Contact */}
            <div className="footer-col newsletter-col">
              <h4>Stay Updated</h4>
              <p>Subscribe to our newsletter for the latest property deals and market trends.</p>
              <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="newsletter-input"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                />
                <button type="submit" className="newsletter-btn" disabled={newsletterStatus === 'loading'}>
                  {newsletterStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
              {newsletterStatus === 'success' && (
                <p style={{ color: '#22c55e', fontSize: '0.875rem', marginTop: '0.5rem', fontWeight: '500' }}>
                  ✓ Thank you for subscribing!
                </p>
              )}
              {newsletterStatus === 'error' && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem', fontWeight: '500' }}>
                  Something went wrong. Please try again.
                </p>
              )}
              <div className="contact-info-footer">
                <p>Email: <a href={`mailto:${supportEmail}`} style={{ color: 'inherit' }}>{supportEmail}</a></p>
                <p>Phone: <a href={`tel:${phoneTel}`} style={{ color: 'inherit' }}>{phoneDisplay}</a></p>
                <p>Web: <a href={websiteUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{websiteUrl.replace(/^https?:\/\//i, '')}</a></p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 No Brokerage. All Rights Reserved.</p>
            <div className="footer-bottom-links">
              <span>Made with ❤️ in India</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Global Lead Generation Components */}
      <WhatsAppFloater />
      <MobileStickyBar />
      <LeadPopup />
      <LoginPopup />
    </div>
  );
};

export default MainLayout;
